import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaTimes, FaCopy, FaCheck, FaUser, FaEnvelope } from "react-icons/fa";
import { fetchTicketDetail } from "../../api/overviewApi";
import { formatAbsoluteDate } from "../utils/formatDate";

// ── Priority resolution ───────────────────────────────────────────────────────
const PRIORITY_ID_MAP = {
  1: "Low",
  2: "Medium",
  3: "High",
  4: "Emergency",
  5: "Critical",
};

const resolvePriority = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "Unknown";
  const num = Number(raw);
  if (!isNaN(num) && num > 0 && PRIORITY_ID_MAP[num]) return PRIORITY_ID_MAP[num];
  return String(raw);
};

export const getPriorityStyle = (priority) => {
  const p = resolvePriority(priority).toLowerCase();
  if (p.includes("emergency") || p.includes("critical"))
    return { background: "rgba(239,68,68,0.15)", color: "#ef4444", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  if (p.includes("high"))
    return { background: "rgba(234,179,8,0.15)", color: "#eab308", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  if (p.includes("medium"))
    return { background: "rgba(59,130,246,0.15)", color: "#3b82f6", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  if (p.includes("low"))
    return { background: "rgba(16,185,129,0.15)", color: "#10b981", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  return { background: "rgba(148,163,184,0.15)", color: "var(--color-text-light)", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
};

export const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("acknowledged") || s.includes("versetal") || s.includes("note"))
    return { background: "rgba(59,130,246,0.15)", color: "#3b82f6", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  if (s.includes("waiting"))
    return { background: "rgba(249,115,22,0.15)", color: "#f97316", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
  return { background: "rgba(148,163,184,0.15)", color: "var(--color-text-light)", padding: "4px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, display: "inline-block" };
};

// ── Modal ─────────────────────────────────────────────────────────────────────
const TicketDetailModal = ({ ticket, onClose }) => {
  const [copied, setCopied]               = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [ticketDetail, setTicketDetail]   = useState(null);

  // Lock body scroll
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  // Fetch full ticket details (queue, assigned-to, description) from backend.
  // ticket_number is the Autotask ref (e.g. "T20260330.0400") — this is what
  // the backend get_ticket_detail view queries against spog_dashboard_main_table.
  // For notification tickets, ticket_number comes from get_notification_list.
  useEffect(() => {
    // Use ticket_number (Autotask ref) — preferred lookup key
    // Fall back to numeric id only if ticket_number is missing
    const lookupKey = ticket?.ticket_number || ticket?.ticket_id || ticket?.id;

    if (!lookupKey) {
      console.warn("[TicketDetailModal] No lookup key available — queue/description won't load");
      return;
    }

    setLoadingDetail(true);
    setTicketDetail(null); // reset on ticket change

    fetchTicketDetail(lookupKey)
      .then((d) => {
        if (d && !d.error) setTicketDetail(d);
      })
      .catch((e) => console.error("[TicketDetailModal] fetchTicketDetail error:", e))
      .finally(() => setLoadingDetail(false));
  }, [ticket?.ticket_number, ticket?.ticket_id, ticket?.id]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleViewTicket = (ticketId, ticketNumber) => {
    const id = ticketNumber || ticketId;
    if (!id) return;
    const base     = process.env.REACT_APP_CLIENT_PORTAL_BASE_URL || "https://versetalinfo.itclientportal.com";
    const template = process.env.REACT_APP_CLIENT_PORTAL_TICKET_PATH || "/ClientPortalMvc/Ticket/Details/{ticket_number}";
    const path     = template.replace("{ticket_number}", id);
    window.open(`${base}/ClientPortal/Login.aspx?ReturnUrl=${encodeURIComponent(path)}&fromLogin=1`, "_blank");
  };

  // ── Resolved display values ─────────────────────────────────────────────────

  // Priority: resolve numeric ID → label
  const displayPriority = resolvePriority(ticket.priority);

  // Title: ticket prop, then API detail
  const displayTitle = ticket.title || ticketDetail?.title || null;

  // Queue: ticket prop (RecentOpenTickets sets this), then API detail
  // ticketDetail.queue comes from spog_dashboard_main_table.queue_name via get_ticket_detail
  const displayQueue = (ticket.queue && ticket.queue !== "Unknown")
    ? ticket.queue
    : (ticketDetail?.queue || null);

  // Description: ticket prop, then API detail
  const displayDescription = ticket.description || ticketDetail?.description || null;

  // Date label logic: recent-open tickets have `created`, notification tickets have `last_activity_date`
  const hasCreated       = Boolean(ticket.created && ticket.created !== "Unknown");
  const displayDateLabel = hasCreated ? "CREATED" : "LAST ACTIVITY";
  const displayDate      = hasCreated ? ticket.created : (ticket.last_activity_date || null);

  // Absolute date for the hover tooltip — only recent-open tickets carry the
  // raw ISO timestamp (`created_date`) behind the relative "created" string;
  // notification tickets' `last_activity_date` is already an absolute
  // timestamp, so there's nothing further to reveal on hover there.
  const displayDateTooltip = hasCreated ? formatAbsoluteDate(ticket.created_date) : null;

  // Assigned to: from detail API only
  const displayAssignedFirst = ticketDetail?.first_name || null;
  const displayAssignedLast  = ticketDetail?.last_name  || "";
  const displayAssignedEmail = ticketDetail?.email_id   || null;

  const displayTicketRef = ticket.ticket_number || ticket.id || ticket.ticket_id || "—";

  return (
    <motion.div
      key="ticket-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.55)", zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <motion.div
        key="ticket-modal-content"
        initial={{ opacity: 0, scale: 0.92, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 16 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        style={{
          background: "var(--color-bg-card)", borderRadius: 12,
          width: "min(95vw, 650px)",
          maxHeight: "90vh",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)", border: "1px solid var(--color-border)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header (fixed) ── */}
        <div style={{ padding: "20px 24px", background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text)" }}>Ticket:</span>
            <button
              onClick={() => handleViewTicket(ticket.ticket_id || ticket.id, ticket.ticket_number)}
              style={{ fontWeight: 600, color: "#2563eb", fontSize: 16, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#1d4ed8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#2563eb"; }}
            >
              {displayTicketRef}
            </button>
            <button
              onClick={() => handleCopy(displayTicketRef)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", color: copied ? "#10b981" : "var(--color-text-light)", borderRadius: 4 }}
              title="Copy Ticket ID"
              onMouseEnter={(e) => { if (!copied) { e.currentTarget.style.color = "var(--color-text)"; e.currentTarget.style.background = "var(--color-border)"; } }}
              onMouseLeave={(e) => { if (!copied) { e.currentTarget.style.color = "var(--color-text-light)"; e.currentTarget.style.background = "none"; } }}
            >
              {copied ? <FaCheck size={14} /> : <FaCopy size={14} />}
            </button>
          </div>
          <button
            onClick={onClose}
            style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)", cursor: "pointer", color: "var(--color-text)", width: 32, height: 32, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-text)"; e.currentTarget.style.background = "var(--color-border)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text)"; e.currentTarget.style.background = "var(--color-bg)"; }}
            title="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>

        {/* ── Title ── */}
        {displayTitle && (
          <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--color-border)" }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>TITLE</span>
            </div>
            <span style={{ color: "var(--color-text)", fontSize: 15, fontWeight: 500, lineHeight: 1.5, display: "block" }}>
              {displayTitle}
            </span>
          </div>
        )}

        {/* ── Details grid ── */}
        <div style={{ padding: "24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px 24px", marginBottom: 20 }}>

            {/* Priority */}
            <div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Priority</span>
              </div>
              <span style={{ ...getPriorityStyle(displayPriority), padding: "4px 12px", borderRadius: 12 }}>
                {displayPriority}
              </span>
            </div>

            {/* Status */}
            <div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</span>
              </div>
              <span style={{ ...getStatusStyle(ticket.status), padding: "4px 12px", borderRadius: 12 }}>
                {ticket.status || "Unknown"}
              </span>
            </div>

            {/* Queue */}
            <div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Queue</span>
              </div>
              {loadingDetail && !displayQueue ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid var(--color-border)", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ fontSize: 12, color: "var(--color-text-light)" }}>Loading...</span>
                </div>
              ) : (
                <span style={{ color: "var(--color-text)", fontWeight: 500, fontSize: 13, border: "1px solid var(--color-border)", background: "var(--color-bg)", padding: "3px 8px", borderRadius: "12px", display: "inline-block" }}>
                  {displayQueue || "—"}
                </span>
              )}
            </div>

            {/* Date */}
            {displayDate && displayDate !== "Unknown" && (
              <div title={displayDateTooltip || undefined}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {displayDateLabel}
                  </span>
                </div>
                <span style={{ color: "var(--color-text)", fontWeight: 500, fontSize: 13, background: "var(--color-bg)", padding: "3px 8px", borderRadius: 6 }}>
                  {displayDate}
                </span>
              </div>
            )}

            {/* Assigned To */}
            <div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Assigned To</span>
              </div>
              {loadingDetail ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid var(--color-border)", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ fontSize: 12, color: "var(--color-text-light)" }}>Searching...</span>
                </div>
              ) : displayAssignedFirst ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ color: "var(--color-text)", fontWeight: 500, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <FaUser size={10} color="var(--color-text-light)" />
                    {displayAssignedFirst} {displayAssignedLast}
                    {displayAssignedEmail && (
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`${displayTicketRef}: ${displayTitle || ""}`);
                          window.location.href = `mailto:${displayAssignedEmail}?subject=${subject}`;
                        }}
                        style={{ background: "none", border: "none", padding: "2px 4px", cursor: "pointer", display: "flex", alignItems: "center", color: "#3b82f6" }}
                        title={`Email ${displayAssignedFirst}`}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.borderRadius = "4px"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#3b82f6"; e.currentTarget.style.background = "none"; }}
                      >
                        <FaEnvelope size={11} />
                      </button>
                    )}
                  </span>
                  {displayAssignedEmail && (
                    <span style={{ color: "var(--color-text-light)", fontSize: 11, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {displayAssignedEmail}
                    </span>
                  )}
                </div>
              ) : (
                <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Unassigned</span>
              )}
            </div>

          </div>

          {/* Description */}
          {/* Always render the section — show spinner while loading, content when ready */}
          <div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.05em" }}>DESCRIPTION</span>
            </div>
            {loadingDetail && !displayDescription ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 6, minHeight: 60 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid var(--color-border)", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: "var(--color-text-light)" }}>Loading description...</span>
              </div>
            ) : displayDescription ? (
              <div
                style={{ minHeight: 60, color: "var(--color-text)", fontSize: 14, lineHeight: 1.6, padding: "12px 16px", background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {displayDescription}
              </div>
            ) : (
              <div style={{ padding: "12px 16px", background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 6, minHeight: 60, display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--color-text-light)" }}>No description available</span>
              </div>
            )}
          </div>
        </div>
        </div>{/* end scrollable body */}
      </motion.div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </motion.div>
  );
};

export default TicketDetailModal;
